import { Fragment } from 'react';
import { Calendar, CheckCircle2, Sparkles, Info } from 'lucide-react';
import type { SchedulingStep } from '../types';
import { STEP_LABELS, STEP_ORDER } from '../constants';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

interface SchedulingHeaderProps {
  currentStep: SchedulingStep;
}

export function SchedulingHeader({ currentStep }: SchedulingHeaderProps) {
  const currentIndex = STEP_ORDER.indexOf(currentStep);

  return (
    <div className="mb-6">
      <div className="flex items-center gap-3 mb-4">
        <Calendar className="h-6 w-6 text-primary" />
        <h1 className="text-2xl font-bold text-foreground">Schedule Posts</h1>

        {/* Prototype Badge with Popover */}
        <Popover>
          <PopoverTrigger asChild>
            <button className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-600 dark:text-amber-400 hover:bg-amber-500/20 transition-colors cursor-help group">
              <Sparkles className="h-3.5 w-3.5" />
              <span className="text-xs font-semibold uppercase tracking-wide">Prototype</span>
              <Info className="h-3 w-3 opacity-60 group-hover:opacity-100 transition-opacity" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-80" align="start">
            <div className="space-y-3">
              <div className="flex items-start gap-2">
                <Sparkles className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-sm text-foreground mb-1">
                    Prototype Feature
                  </h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    This scheduling interface is currently a <strong>working prototype</strong>.
                    While you can explore the UI and workflow, posts won't be scheduled to your
                    actual social media accounts yet.
                  </p>
                </div>
              </div>

              <div className="pt-2 border-t border-border">
                <p className="text-xs text-muted-foreground">
                  <strong className="text-foreground">Coming soon:</strong> Full integration with
                  Kontentino's scheduling system for real post publishing.
                </p>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {/* Step indicator */}
      <div className="flex items-center gap-2">
        {STEP_ORDER.slice(0, 3).map((step, index) => {
          const isActive = step === currentStep;
          const isCompleted = index < currentIndex;

          return (
            <Fragment key={step}>
              <div
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full transition-colors ${
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : isCompleted
                      ? 'bg-primary/20 text-primary'
                      : 'bg-muted text-muted-foreground'
                }`}
              >
                {isCompleted && <CheckCircle2 className="h-4 w-4" />}
                <span className="text-sm font-medium">{STEP_LABELS[step]}</span>
              </div>

              {index < 2 && (
                <div
                  className={`h-0.5 w-8 transition-colors ${
                    isCompleted ? 'bg-primary' : 'bg-muted'
                  }`}
                />
              )}
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}
